import React, { useState, useEffect } from 'react';
import { Button, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import { AiOutlinePlus } from 'react-icons/ai';
import axios from '../utils/api';
import RichTextEditor from './RichTextEditor';
import DraggableProjectsList from './DraggableProjectsList';
import './AdminDashboard.css';

function ProjectsEditor() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imgPath: '',
    ghLink: '',
    demoLink: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/content/projects');
      // Projects are already sorted by display_order from API
      const sortedProjects = (response.data.data || []).sort((a, b) => {
        const orderA = a.display_order ?? a.id ?? 999;
        const orderB = b.display_order ?? b.id ?? 999;
        return orderA - orderB;
      });
      setProjects(sortedProjects);
    } catch (err) {
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        title: project.title || '',
        description: project.description || '',
        imgPath: project.img_path || '',
        ghLink: project.gh_link || '',
        demoLink: project.demo_link || ''
      });
    } else {
      setEditingProject(null);
      setFormData({
        title: '',
        description: '',
        imgPath: '',
        ghLink: '',
        demoLink: ''
      });
    }
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProject(null);
    setFormData({
      title: '',
      description: '',
      imgPath: '',
      ghLink: '',
      demoLink: ''
    });
    setError('');
    setSuccess('');
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only image files are allowed (JPEG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64String = reader.result;
          const response = await axios.post('/upload', {
            filename: file.name,
            contentType: file.type,
            file: base64String
          });

          if (response.data.success) {
            // Update the form data with the blob URL
            setFormData({ ...formData, imgPath: response.data.url });
            setSuccess('Image uploaded successfully!');
          } else {
            setError(response.data.error || 'Upload failed');
          }
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to upload image');
        } finally {
          setUploading(false);
        }
      };
      reader.onerror = () => {
        setError('Failed to read file');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to upload image');
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingProject) {
        await axios.put('/content/projects', {
          id: editingProject.id,
          ...formData
        });
        setSuccess('Project updated successfully');
      } else {
        await axios.post('/content/projects', formData);
        setSuccess('Project created successfully');
      }
      
      handleCloseModal();
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save project');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await axios.delete('/content/projects', { data: { id } });
      setSuccess('Project deleted successfully');
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete project');
    }
  };

  const handleReorder = async (updatedOrders) => {
    setReordering(true);
    setError('');
    setSuccess('');
    
    try {
      // Optimistically update the local state
      const sortedProjects = [...projects].sort((a, b) => {
        const orderA = updatedOrders.find(o => o.id === a.id)?.display_order ?? a.display_order ?? 999;
        const orderB = updatedOrders.find(o => o.id === b.id)?.display_order ?? b.display_order ?? 999;
        return orderA - orderB;
      });
      setProjects(sortedProjects);

      // Update the server
      await axios.patch('/content/projects', {
        action: 'reorder',
        projectOrders: updatedOrders
      });
      
      setSuccess('Project order updated successfully!');
      // Refresh to ensure consistency
      setTimeout(() => fetchProjects(), 500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update project order');
      // Revert on error
      fetchProjects();
    } finally {
      setReordering(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading projects...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="editor-card">
      {error && <Alert variant="danger" className="alert-admin" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" className="alert-admin" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <div className="editor-header">
        <h3>📁 Manage Projects</h3>
        <Button className="btn-primary-admin" onClick={() => handleOpenModal()}>
          <AiOutlinePlus style={{ marginRight: '0.5rem' }} />
          Add New Project
        </Button>
      </div>

      {projects.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📭</div>
          <h4>No projects yet</h4>
          <p>Get started by adding your first project!</p>
          <Button className="btn-primary-admin mt-3" onClick={() => handleOpenModal()}>
            <AiOutlinePlus style={{ marginRight: '0.5rem' }} />
            Add Your First Project
          </Button>
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f8f9ff', borderRadius: '8px', border: '1px solid #667eea' }}>
            <strong style={{ color: '#667eea' }}>📋 Drag and drop projects to reorder them</strong>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
              The order you set here will be reflected on your portfolio. Click and drag the grip icon (⋮⋮) to move projects.
            </p>
          </div>
          {reordering && (
            <Alert variant="info" className="alert-admin" style={{ marginBottom: '1rem' }}>
              Updating project order...
            </Alert>
          )}
          <DraggableProjectsList
            projects={projects}
            onReorder={handleReorder}
            onEdit={handleOpenModal}
            onDelete={handleDelete}
          />
        </div>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg" className="admin-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editingProject ? '✏️ Edit Project' : '➕ Add New Project'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} className="admin-form">
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                placeholder="Describe your project..."
                rows={4}
              />
              <Form.Text className="text-muted">Use the toolbar above to format your description (Bold, Italic, Lists, etc.).</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Project Image *</Form.Label>
              <div className="d-flex gap-2 mb-2">
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleFileUpload(file);
                  }}
                  disabled={uploading}
                  style={{ flex: 1 }}
                />
                {uploading && <Spinner size="sm" />}
              </div>
              <Form.Control
                type="text"
                value={formData.imgPath}
                onChange={(e) => setFormData({ ...formData, imgPath: e.target.value })}
                required
                placeholder="Upload image above or enter URL/path manually"
              />
              <Form.Text className="text-muted">
                Upload new image or enter path/URL. Existing local paths will continue to work.
              </Form.Text>
              {formData.imgPath && (
                <div className="mt-2">
                  <img 
                    src={formData.imgPath} 
                    alt="Preview" 
                    style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', border: '1px solid #ddd' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>GitHub Link</Form.Label>
              <Form.Control
                type="url"
                value={formData.ghLink}
                onChange={(e) => setFormData({ ...formData, ghLink: e.target.value })}
                placeholder="https://github.com/username/repo"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Demo Link</Form.Label>
              <Form.Control
                type="url"
                value={formData.demoLink}
                onChange={(e) => setFormData({ ...formData, demoLink: e.target.value })}
                placeholder="https://demo.example.com"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} style={{ borderRadius: '10px' }}>
              Cancel
            </Button>
            <Button className="btn-primary-admin" type="submit">
              {editingProject ? '💾 Update Project' : '✨ Create Project'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default ProjectsEditor;

